import { Icon } from '@aibees/shared/components';
import { useCurrentSubscriptionApi } from '@aibees/shared/features/price-and-plan/api/use-current-subscription-api';
import { useCurrentPlanInfo } from '@aibees/shared/features/price-and-plan/hooks/use-current-plan-info';
import { AccountPopoverCreditPlanDetail } from '@aibees/shared/layouts/_common/components/account-popover-credit-plan-detail';
import { classnames, fCredit, toPercent } from '@aibees/shared/utils';
import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import { memo } from 'react';
import * as Styled from './styles';
import * as SX from './sx';
import type { TotalCreditBalanceProps as Props } from './types';

export const TotalCreditBalance = memo(({ className }: Props) => {
  const { data } = useCurrentSubscriptionApi();
  const { credit, rollOver } = useCurrentPlanInfo();

  const subPercentCredit = toPercent(
    credit,
    Number(data?.current?.plan?.metadata.request)
  );
  const subPercentRollover = toPercent(
    rollOver,
    Number(data?.current?.plan?.metadata.rollover)
  );

  const lowOnCredit = () => {
    return (
      toPercent(
        Number(data?.credit?.total),
        Number(data?.current?.plan?.metadata.request)
      ) <= 5
    );
  };

  return (
    <Styled.Wrapper
      className={classnames('TotalCreditBalance', className)}
      sx={SX.root}
      variant="outlined"
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={SX.header}
      >
        <Typography variant="body3" fontWeight={700} color="grey.700">
          Total Credit Balance
        </Typography>
        <AccountPopoverCreditPlanDetail variant="credit-balance" />
      </Stack>
      <Stack direction="row" gap={3} sx={SX.creditInfo}>
        <Stack sx={{ flex: 1 }}>
          <Styled.CreditValueWrapper>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Icon name="coin" color="grey.700" size={16} />
              <Typography variant="body4" fontWeight={600} color="grey.700">
                Plan Credits
              </Typography>
            </Stack>
            <Styled.CreditValueNumberWrapper>
              <Typography variant="body4" fontWeight={700} color="grey.700">
                {fCredit(credit) || 0}
              </Typography>
              /
              <Typography variant="body4" fontWeight={600} color="grey.700">
                {fCredit(Number(data?.current?.plan?.metadata.request) || 0)}
              </Typography>
            </Styled.CreditValueNumberWrapper>
          </Styled.CreditValueWrapper>
          <Box>
            <LinearProgress
              color={lowOnCredit() ? 'warning' : 'primary'}
              variant="determinate"
              value={subPercentCredit}
            />
          </Box>
        </Stack>
        {!data?.current?.plan?.metadata.is_free && (
          <Stack sx={{ flex: 1 }}>
            <Styled.CreditValueWrapper>
              <Stack direction="row" alignItems="center" gap={0.5}>
                <Icon name="coin" color="grey.700" size={16} />
                <Typography variant="body4" fontWeight={600} color="grey.700">
                  Roll Over Credits
                </Typography>
              </Stack>
              <Styled.CreditValueNumberWrapper>
                <Typography variant="body4" fontWeight={700} color="grey.700">
                  {fCredit(Number(rollOver) || 0)}
                </Typography>
                /
                <Typography variant="body4" fontWeight={600} color="grey.700">
                  {fCredit(Number(data?.current?.plan?.metadata.rollover))}
                </Typography>
              </Styled.CreditValueNumberWrapper>
            </Styled.CreditValueWrapper>
            <Box>
              <LinearProgress
                color="success"
                variant="determinate"
                value={subPercentRollover}
              />
            </Box>
          </Stack>
        )}
      </Stack>
    </Styled.Wrapper>
  );
});

TotalCreditBalance.displayName = 'TotalCreditBalance';
